import { useRef, useState } from "react";
import { useTasks, type TaskPriority, type TaskComplexity, type TaskReference, type ReferenceKind } from "../store/tasks";
import { DEPARTMENTS, ALL_DEPTS } from "../data/org";
import { CLIENTS } from "../data/clients";
import { useSettings } from "../store/settings";
import { estimateHoursAI } from "../lib/claude";
import { AIImproveButton } from "./AIImproveButton";
import { resizeForReference } from "../lib/imageResize";

interface DraftTask {
  id: string;
  title: string;
  description: string;
  clientId: string;
  targetDepts: string[];
  priority: TaskPriority;
  complexity: TaskComplexity;
  estimatedHours: number;
  references: TaskReference[];
}

const uid = () => "d" + Math.random().toString(36).slice(2, 10);

function emptyDraft(clientId = ""): DraftTask {
  return {
    id: uid(),
    title: "",
    description: "",
    clientId,
    targetDepts: [],
    priority: "normal",
    complexity: "medium",
    estimatedHours: 0,
    references: []
  };
}

export function MeetingTaskBuilder({ defaultClientId = "" }: { defaultClientId?: string }) {
  const create = useTasks(s => s.create);
  const settings = useSettings();
  const [drafts, setDrafts] = useState<DraftTask[]>([emptyDraft(defaultClientId)]);
  const [busyId, setBusyId] = useState<string | null>(null);

  const update = (id: string, patch: Partial<DraftTask>) =>
    setDrafts(ds => ds.map(d => d.id === id ? { ...d, ...patch } : d));

  const remove = (id: string) =>
    setDrafts(ds => ds.length === 1 ? ds : ds.filter(d => d.id !== id));

  const add = () => setDrafts(ds => [...ds, emptyDraft(ds[ds.length-1]?.clientId ?? defaultClientId)]);

  const estimate = async (d: DraftTask) => {
    if (!d.title.trim()) { alert("Título primeiro."); return; }
    if (!settings.anthropicKey) { alert("Configure chave Anthropic em ⚙️."); return; }
    setBusyId(d.id);
    try {
      const r = await estimateHoursAI({
        apiKey: settings.anthropicKey,
        title: d.title, description: d.description,
        targetDepts: d.targetDepts, complexity: d.complexity
      });
      update(d.id, { estimatedHours: r.hours });
    } catch (e) { alert("Falha: " + (e as Error).message); }
    finally { setBusyId(null); }
  };

  const dispatch = () => {
    const valid = drafts.filter(d => d.title.trim());
    if (valid.length === 0) { alert("Nenhuma tarefa preenchida."); return; }
    let count = 0;
    valid.forEach(d => {
      create({
        title: d.title.trim(),
        description: d.description.trim(),
        clientId: d.clientId || undefined,
        targetDepts: d.targetDepts,
        assignedTo: [],
        priority: d.priority,
        complexity: d.complexity,
        estimatedHours: d.estimatedHours > 0 ? d.estimatedHours : undefined,
        references: d.references,
        stage: "draft",
        tags: ["meeting"],
        status: "pending"
      });
      count++;
    });
    setDrafts([emptyDraft(defaultClientId)]);
    alert(`✓ ${count} demanda${count===1?"":"s"} enviada${count===1?"":"s"} pro Quadro.`);
  };

  const totalHours = drafts.reduce((a, d) => a + (d.estimatedHours || 0), 0);

  return (
    <div className="space-y-3">
      <div className="border-2 border-ink bg-gold/10 px-4 py-3 flex items-center justify-between">
        <div>
          <div className="font-display font-bold uppercase">▸ Criar tarefas da reunião</div>
          <div className="font-crimson italic text-paper text-sm">
            digite, melhore com IA, anexe referências, estime tempo → tudo vai pro Quadro
          </div>
        </div>
        <div className="text-right">
          <div className="tag text-graydk">total estimado</div>
          <div className="font-display font-black text-2xl">{totalHours.toFixed(1)}h</div>
        </div>
      </div>

      {drafts.map((d, i) => (
        <DraftCard
          key={d.id}
          index={i + 1}
          draft={d}
          busy={busyId === d.id}
          onUpdate={(p) => update(d.id, p)}
          onRemove={() => remove(d.id)}
          onEstimate={() => estimate(d)}
        />
      ))}

      <div className="flex gap-2 justify-between">
        <button onClick={add} className="btn btn-fire">+ outra tarefa</button>
        <button onClick={dispatch} className="btn bg-ink text-bone border-ink">
          ✓ enviar {drafts.filter(d => d.title.trim()).length} pro Quadro
        </button>
      </div>
    </div>
  );
}

function DraftCard({ draft, index, busy, onUpdate, onRemove, onEstimate }:
  { draft: DraftTask; index: number; busy: boolean;
    onUpdate: (p: Partial<DraftTask>) => void; onRemove: () => void; onEstimate: () => void }) {

  const fileInput = useRef<HTMLInputElement>(null);
  const [showRefForm, setShowRefForm] = useState(false);
  const [refKind, setRefKind] = useState<ReferenceKind>("link");
  const [refUrl, setRefUrl] = useState("");
  const [refName, setRefName] = useState("");

  const ctx = `Cliente: ${draft.clientId || "interno"}. Depts: ${draft.targetDepts.join(", ") || "qualquer"}.`;

  const addRef = (ref: Omit<TaskReference, "id">) => {
    onUpdate({ references: [...draft.references, { ...ref, id: "r" + Math.random().toString(36).slice(2, 9) }] });
    setShowRefForm(false);
    setRefUrl(""); setRefName("");
  };

  const onUploadFile = (f: File) => {
    if (f.size > 16 * 1024 * 1024) { alert("Arquivo > 16MB."); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const kind: ReferenceKind =
        f.type.startsWith("image/") ? "image" :
        f.type.startsWith("audio/") ? "audio" :
        f.type.startsWith("video/") ? "video" : "file";
      let url = String(reader.result);
      if (kind === "image") {
        try { url = await resizeForReference(url); } catch {}
      } else if (kind === "audio" || kind === "video") {
        // áudio/vídeo grandes não cabem em localStorage —
        // avisar e pedir URL externa
        if (url.length > 1_500_000) {
          alert(`${kind} grande demais pra localStorage (${(url.length/1024/1024).toFixed(1)}MB). Suba no Drive/MinIO e cole o link em "+ link" em vez disso.`);
          return;
        }
      }
      addRef({ kind, url, name: f.name });
    };
    reader.readAsDataURL(f);
  };

  const removeRef = (id: string) =>
    onUpdate({ references: draft.references.filter(r => r.id !== id) });

  const toggleDept = (id: string) => {
    const next = draft.targetDepts.includes(id)
      ? draft.targetDepts.filter(x => x !== id)
      : [...draft.targetDepts, id];
    onUpdate({ targetDepts: next });
  };

  return (
    <div className="border-2 border-ink bg-bone p-3 relative">
      <div className="flex items-center justify-between mb-2">
        <span className="tag bg-ink text-bone px-1.5">#{String(index).padStart(2, "0")}</span>
        <button onClick={onRemove} className="tag text-red underline">remover</button>
      </div>

      {/* título */}
      <div className="mb-2">
        <div className="tag text-graydk mb-1 flex items-center justify-between">
          <span>título</span>
          <AIImproveButton
            value={draft.title}
            mode="title"
            context={ctx}
            onApply={(v) => onUpdate({ title: v })}
          />
        </div>
        <input
          value={draft.title}
          onChange={(e) => onUpdate({ title: e.target.value })}
          placeholder="ex: Criar carrossel de 10 slides para Black Friday R3 Rental"
          className="w-full border border-ink bg-bone px-2 py-1.5 font-display font-bold text-lg uppercase"
        />
      </div>

      {/* descrição */}
      <div className="mb-2">
        <div className="tag text-graydk mb-1 flex items-center justify-between">
          <span>briefing / prompt</span>
          <div className="flex gap-1">
            <AIImproveButton
              value={draft.description}
              mode="brief"
              context={`Título: ${draft.title}. ${ctx}`}
              onApply={(v) => onUpdate({ description: v })}
              label="🪄 melhorar briefing"
            />
            <AIImproveButton
              value={draft.description}
              mode="prompt"
              context={`Título: ${draft.title}. ${ctx}`}
              onApply={(v) => onUpdate({ description: v })}
              label="🪄 virar prompt"
            />
          </div>
        </div>
        <textarea
          value={draft.description}
          onChange={(e) => onUpdate({ description: e.target.value })}
          rows={3}
          placeholder="contexto, objetivo, restrições, prazo, formato esperado…"
          className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[13px] resize-y"
        />
      </div>

      {/* cliente + depts */}
      <div className="grid md:grid-cols-2 gap-2 mb-2">
        <div>
          <div className="tag text-graydk mb-1">cliente</div>
          <select value={draft.clientId} onChange={(e) => onUpdate({ clientId: e.target.value })}
                  className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px]">
            <option value="">— interno —</option>
            {CLIENTS.map(c => <option key={c.id} value={c.id}>{c.shortName}</option>)}
          </select>
        </div>
        <div>
          <div className="tag text-graydk mb-1">deptos-alvo (clique)</div>
          <div className="flex flex-wrap gap-1">
            {DEPARTMENTS.map(dpt => (
              <button key={dpt.id} type="button" onClick={() => toggleDept(dpt.id)}
                className={`tag px-1.5 py-0.5 border border-ink
                  ${draft.targetDepts.includes(dpt.id) ? "bg-ink text-bone" : "bg-bone"}`}>
                {dpt.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* priority + complexity + estimate */}
      <div className="grid md:grid-cols-3 gap-2 mb-2">
        <div>
          <div className="tag text-graydk mb-1">prioridade</div>
          <select value={draft.priority} onChange={(e) => onUpdate({ priority: e.target.value as TaskPriority })}
                  className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px]">
            <option value="low">low</option><option value="normal">normal</option>
            <option value="high">high</option><option value="urgent">urgent</option>
          </select>
        </div>
        <div>
          <div className="tag text-graydk mb-1">complexidade</div>
          <select value={draft.complexity} onChange={(e) => onUpdate({ complexity: e.target.value as TaskComplexity })}
                  className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px]">
            <option value="trivial">trivial</option><option value="low">low</option>
            <option value="medium">medium</option><option value="high">high</option>
            <option value="critical">critical</option>
          </select>
        </div>
        <div>
          <div className="tag text-graydk mb-1 flex items-center justify-between">
            <span>estimativa (horas)</span>
            <button type="button" onClick={onEstimate} disabled={busy}
              className="tag underline text-ink disabled:opacity-50">
              {busy ? "…" : "🪄 estimar IA"}
            </button>
          </div>
          <input type="number" step="0.5" min="0" value={draft.estimatedHours}
                 onChange={(e) => onUpdate({ estimatedHours: Number(e.target.value) })}
                 className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px]" />
        </div>
      </div>

      {/* references */}
      <div className="border-t border-ink/20 pt-2">
        <div className="flex items-center justify-between mb-1">
          <div className="tag text-graydk">referências ({draft.references.length})</div>
          <div className="flex gap-1">
            <button type="button" onClick={() => fileInput.current?.click()}
                    className="tag underline">📎 upload</button>
            <button type="button" onClick={() => setShowRefForm(s => !s)}
                    className="tag underline">+ link</button>
            <input ref={fileInput} type="file" accept="image/*,audio/*,video/*"
                   className="hidden"
                   onChange={(e) => { const f = e.target.files?.[0]; if (f) onUploadFile(f); if (e.target) e.target.value = ""; }} />
          </div>
        </div>

        {showRefForm && (
          <div className="border border-ink p-2 mb-2 flex gap-1 items-end flex-wrap">
            <select value={refKind} onChange={(e) => setRefKind(e.target.value as ReferenceKind)}
                    className="border border-ink bg-bone px-1 py-1 font-mono text-[11px]">
              <option value="link">link</option>
              <option value="image">imagem (URL)</option>
              <option value="audio">áudio (URL)</option>
              <option value="video">vídeo (URL)</option>
            </select>
            <input value={refUrl} onChange={(e) => setRefUrl(e.target.value)}
                   placeholder="URL"
                   className="flex-1 min-w-[160px] border border-ink bg-bone px-1 py-1 font-mono text-[11px]" />
            <input value={refName} onChange={(e) => setRefName(e.target.value)}
                   placeholder="rótulo (opcional)"
                   className="w-32 border border-ink bg-bone px-1 py-1 font-mono text-[11px]" />
            <button type="button"
                    onClick={() => { if (refUrl.trim()) addRef({ kind: refKind, url: refUrl.trim(), name: refName.trim() || undefined }); }}
                    className="btn btn-hire">+</button>
          </div>
        )}

        {draft.references.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
            {draft.references.map(r => (
              <div key={r.id} className="border border-ink p-1 bg-bone text-[10px] font-mono flex items-start gap-1">
                {r.kind === "image" ? (
                  <img src={r.url} alt="" className="w-12 h-12 object-cover" />
                ) : (
                  <span className="w-12 h-12 bg-ink text-bone flex items-center justify-center text-lg">
                    {r.kind === "audio" ? "♪" : r.kind === "video" ? "▶" : r.kind === "file" ? "▤" : "↗"}
                  </span>
                )}
                <div className="flex-1 min-w-0">
                  <div className="tag bg-ink text-bone px-1">{r.kind}</div>
                  <div className="truncate">{r.name ?? r.url.slice(0, 24)}</div>
                  <button onClick={() => removeRef(r.id)} className="text-red underline">×</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
